import React from "react";
import { Link } from "react-router-dom";
import { useConsultationDetailModule } from "./hooks/consultation_detail_module.hook";
import style from "./consultation_detail.module.css";

export const DoctorConsultationDetail: React.FC = () => {
  const {
    id,
    consultation,
    notes,
    prescription,
    patientInfo,
    loadingPatient,
    isSharing,
    initials,
    hasShareableContent,
    showRejoin,
    isRejoinExpired,
    formatDuration,
    handleShare,
    handleViewUploadedPrescription,
    handleViewClinicalNotesFile,
    navigate,
    toast,
  } = useConsultationDetailModule();

  return (
    <div className={style.doctorBody}>
      {toast.show && (
        <div className={`${style.toastMsg} ${style.show} ${toast.type === "success" ? style.toastSuccess : style.toastError}`}>
          <span className="material-icons-round">{toast.type === "success" ? "check_circle" : "error"}</span>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className={style.docHeader} style={{ position: "sticky", top: 0, zIndex: 100 }}>
        <button className={style.docHeaderBack} onClick={() => navigate(-1)}>
          <span className="material-icons-round">arrow_back</span>
        </button>
        <div className={style.docHeaderTitle}>
          <h1>Consultation Details</h1>
          <span>{consultation?.ticket_id ? String(consultation.ticket_id) : "Loading..."}</span>
        </div>
        <img
          src="/logo-transparent3.png"
          alt="Pococare"
          style={{ height: "36px" }}
          onError={(e) => {
            (e.target as HTMLImageElement).src =
              "https://nice-stone-091e25e00.3.azurestaticapps.net/logo-transparent3.png";
          }}
        />
      </div>

      {/* Content */}
      <div style={{ padding: "14px", display: "flex", flexDirection: "column", gap: "12px", paddingBottom: "32px" }}>

        {/* Patient Hero Card */}
        <div className={style.detailHeroCard}>
          <div className={style.detailHeroTop}>
            <div className={style.detailPatientAvatar}>{initials}</div>
            <div className={style.detailHeroInfo}>
              <div className={style.heroName}>{consultation?.patient_name || "Patient"}</div>
              <div className={style.heroMeta}>
                {consultation?.patient_age || "N/A"} yrs • {consultation?.patient_gender || "N/A"}
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "8px" }}>
              <span className={`${style.detailStatusPill} ${consultation?.status === "completed" ? style.pillCompleted : style.pillCancelled}`}>
                {consultation?.status === "completed" ? "✓ Completed" : consultation?.status === "cancelled" ? "✗ Cancelled" : consultation?.status || ""}
              </span>
              {showRejoin && (
                <button
                  onClick={() => {
                    if (!isRejoinExpired) {
                      navigate(`/consultation/${id}`);
                    }
                  }}
                  disabled={isRejoinExpired}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    padding: "6px 12px",
                    background: isRejoinExpired ? "#e0e0e0" : "#fff",
                    color: isRejoinExpired ? "#9e9e9e" : "var(--primary, #0B6E4F)",
                    border: isRejoinExpired ? "1.5px solid #ccc" : "1.5px solid var(--primary, #0B6E4F)",
                    borderRadius: "20px",
                    fontFamily: "'Inter', sans-serif",
                    fontSize: "12px",
                    fontWeight: 700,
                    cursor: isRejoinExpired ? "not-allowed" : "pointer",
                    whiteSpace: "nowrap",
                  }}
                  title={isRejoinExpired ? "Meeting link expired" : "Rejoin video call"}
                >
                  <span className="material-icons-round" style={{ fontSize: "14px" }}>video_call</span>
                  Rejoin
                </button>
              )}
            </div>
          </div>
          <div className={style.detailHeroGrid}>
            {[
              { label: "Date", icon: "calendar_today", value: consultation?.requested_at ? new Date(consultation.requested_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "N/A" },
              { label: "Time", icon: "schedule", value: consultation?.requested_at ? new Date(consultation.requested_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "N/A" },
              { label: "Duration", icon: "timer", value: formatDuration(consultation?.call_duration_seconds) },
              { label: "Mode", icon: "videocam", value: consultation?.consultation_mode ? `${consultation.consultation_mode.charAt(0).toUpperCase() + consultation.consultation_mode.slice(1)} Call` : "Video Call" },
              { 
                label: "Date of Birth", 
                icon: "cake", 
                value: patientInfo?.date_of_birth
                  ? new Date(patientInfo.date_of_birth).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
                  : "N/A" 
              },
              { 
                label: "Blood Group", 
                icon: "water_drop", 
                value: patientInfo?.blood_group || "N/A" 
              },
            ].map(({ label, icon, value }) => (
              <div key={label} className={style.detailHeroStat}>
                <span className={style.statLabel}>{label}</span>
                <span className={style.statValue}>
                  <span className="material-icons-round">{icon}</span>
                  {loadingPatient && (label === "Date of Birth" || label === "Blood Group") ? (
                    <div style={{ display: "inline-flex", alignItems: "center" }}>
                      <div className={style.spinner} />
                    </div>
                  ) : (
                    value
                  )}
                </span>
              </div>
            ))}

          </div>

          {consultation?.status !== "cancelled" && consultation?.status !== "rejected" && (
            <div 
              style={{ 
                padding: "12px 16px", 
                borderTop: "1px solid var(--outline)", 
                background: "var(--surface-container-low, #f8fafd)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px"
              }}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                <span className={style.statLabel} style={{ fontWeight: 700, color: "var(--on-surface)" }}>MEDICAL PROFILE</span>
                <span style={{ fontSize: "12px", color: "var(--on-surface)", fontWeight: 700 }}>
                  View medical history
                </span>
              </div>
              <div>
                {loadingPatient ? (
                  <div style={{ display: "inline-flex", alignItems: "center", gap: "4px", fontSize: "13px", color: "var(--on-surface-variant)" }}>
                    <div className={style.spinner} />
                    Loading...
                  </div>
                ) : patientInfo?.medical_profile_pdf_url ? (
                  <button
                    onClick={() => window.open(patientInfo.medical_profile_pdf_url, "_blank")}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "6px 14px",
                      borderRadius: "8px",
                      background: "#e8f0fe",
                      color: "#1a73e8",
                      border: "1px solid #c2e7ff",
                      fontWeight: 700,
                      fontSize: "13px",
                      cursor: "pointer",
                      whiteSpace: "nowrap"
                    }}
                  >
                    <span className="material-icons-round" style={{ fontSize: "16px" }}>folder_open</span>
                    Open Document
                  </button>
                ) : (
                  <span style={{ fontSize: "13px", fontWeight: 700, color: "var(--on-surface-variant)" }}>N/A</span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Clinical Notes */}
        <div className={style.detailSectionCard}>
          <div className={style.detailSectionHeader}>
            <div className={style.detailSectionHeaderLeft}>
              <span className="material-icons-round">edit_note</span>
              <h2>Clinical Notes</h2>
            </div>
            {consultation?.status !== "cancelled" && consultation?.status !== "rejected" && (
              <Link to={`/consultation/${id}/notes`} className={style.detailEditBtn}>
                <span className="material-icons-round">edit</span>
                Edit
              </Link>
            )}
          </div>
          {notes ? (
            <>
              {notes.chief_complaint && (
                <div className={style.detailNotesRow}>
                  <span className={style.rowLabel}>Chief Complaint</span>
                  <span className={style.rowValue}>{notes.chief_complaint}</span>
                </div>
              )}
              {notes.symptom_duration && (
                <div className={style.detailNotesRow}>
                  <span className={style.rowLabel}>Duration</span>
                  <span className={style.rowValue}>{notes.symptom_duration}</span>
                </div>
              )}
              {notes.symptoms && notes.symptoms.length > 0 && (
                <div className={style.detailNotesRow}>
                  <span className={style.rowLabel}>Symptoms</span>
                  <div className={style.rowValue}>
                    <div className={style.detailTags}>
                      {notes.symptoms.map((s: string) => (
                        <span key={s} className={`${style.detailTag} ${style.tagPrimary}`}>{s}</span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {notes.provisional_diagnosis && (
                <div className={style.detailNotesRow}>
                  <span className={style.rowLabel}>Diagnosis</span>
                  <span className={style.rowValue}>{notes.provisional_diagnosis}</span>
                </div>
              )}
              {(notes.hospitalization_recommended || notes.ambulance_type) && (
                <div className={style.detailNotesRow}>
                  <span className={style.rowLabel}>Recommendations</span>
                  <div className={style.rowValue}>
                    <div className={style.detailTags}>
                      {notes.hospitalization_recommended && (
                        <span className={`${style.detailTag} ${style.tagAlert}`}>Hospitalisation Recommended</span>
                      )}
                      {notes.ambulance_type && notes.ambulance_type !== "None" && (
                        <span className={`${style.detailTag} ${style.tagSuccess}`}>Ambulance: {notes.ambulance_type}</span>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {notes.dos_and_donts && (
                <div className={style.detailNotesRow}>
                  <span className={style.rowLabel}>Dos & Don'ts</span>
                  <span className={style.rowValue}>{notes.dos_and_donts}</span>
                </div>
              )}
              {notes.notes_file_url && (
                <div style={{ padding: "16px 0 0 0", textAlign: "center", borderTop: "1px solid var(--border-light)", marginTop: "12px" }}>
                  <button 
                    onClick={handleViewClinicalNotesFile} 
                    style={{
                      display: "inline-flex", alignItems: "center", gap: "8px",
                      padding: "10px 16px", background: "var(--primary-container)",
                      color: "var(--primary)", border: "none", borderRadius: "10px",
                      fontFamily: "'Inter', sans-serif", fontSize: "13px", fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    <span className="material-icons-round">description</span>
                    View Uploaded Clinical Notes
                  </button>
                </div>
              )}
            </>
          ) : (
            <div style={{ padding: "16px", color: "var(--placeholder)", fontSize: "13px", textAlign: "center" }}>
              No clinical notes saved yet.
            </div>
          )}
        </div>

        {/* Prescription */}
        <div className={style.detailSectionCard}>
          <div className={style.detailSectionHeader}>
            <div className={style.detailSectionHeaderLeft}>
              <span className="material-icons-round">medication</span>
              <h2>Prescription</h2>
            </div>
            {consultation?.status !== "cancelled" && consultation?.status !== "rejected" && (
              <Link to={`/consultation/${id}/prescription`} className={style.detailEditBtn}>
                <span className="material-icons-round">edit</span>
                Edit
              </Link>
            )}
          </div>
          {prescription?.medicines && prescription.medicines.length > 0 && !prescription?.prescription?.prescription_pdf_url?.includes("prescriptions") ? (
            <>
              {prescription.medicines.map((med: any, idx: number) => (
                <div key={idx} className={style.detailMedItem}>
                  <div className={style.detailMedIcon}>
                    <span className="material-icons-round">medication</span>
                  </div>
                  <div className={style.detailMedInfo}>
                    <div className={style.medName}>{med.medicine_name} {med.dosage}</div>
                    <div className={style.medDetails}>
                      {[med.frequency, `${med.duration_days} days`, med.timing, med.instructions].filter(Boolean).join(" • ")}
                    </div>
                  </div>
                </div>
              ))}
              {prescription?.prescription && (
                <div style={{ padding: "16px 0 8px 0", textAlign: "center", borderTop: "1px solid var(--border-light)", marginTop: "12px" }}>
                  <button 
                    onClick={handleViewUploadedPrescription} 
                    style={{
                      display: "inline-flex", alignItems: "center", gap: "8px",
                      padding: "10px 16px", background: "var(--primary-container)",
                      color: "var(--primary)", border: "none", borderRadius: "10px",
                      fontFamily: "'Inter', sans-serif", fontSize: "13px", fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    <span className="material-icons-round">picture_as_pdf</span>
                    View Prescription PDF
                  </button>
                </div>
              )}
            </>
          ) : prescription?.prescription ? (
            <div style={{ padding: "16px", textAlign: "center" }}>
              <button 
                onClick={handleViewUploadedPrescription} 
                style={{
                  display: "inline-flex", alignItems: "center", gap: "8px",
                  padding: "10px 16px", background: "var(--primary-container)",
                  color: "var(--primary)", border: "none", borderRadius: "10px",
                  fontFamily: "'Inter', sans-serif", fontSize: "13px", fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                <span className="material-icons-round">picture_as_pdf</span>
                View Uploaded Prescription
              </button>
            </div>
          ) : (
            <div style={{ padding: "16px", color: "var(--placeholder)", fontSize: "13px", textAlign: "center" }}>
              No prescription saved yet.
            </div>
          )}
        </div>

        {/* Share Button */}
        {hasShareableContent && (
          <button 
            className={style.detailShareBtn} 
            onClick={() => handleShare("whatsapp")}
            disabled={isSharing}
          >
            <span className="material-icons-round">
              {isSharing ? "hourglass_top" : "share"}
            </span>
            {isSharing ? "Sharing via WhatsApp..." : "Share Clinical Notes & Prescription"}
          </button>
        )}

        <button className={style.detailBackBtn} onClick={() => navigate(-1)}>
          <span className="material-icons-round">arrow_back</span>
          Back to Consultation History
        </button>
      </div>
    </div>
  );
};
